import React, { useMemo, useState } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

/**
 * Tiny, reusable sort utility for table-based lists.
 *
 * Usage:
 *   const { sortKey, sortDir, toggle, sorted } = useTableSort(items, "label", "asc", {
 *       // optional custom value resolvers per key
 *       monat: (t) => (t.pauschalmiete ?? (t.kaltmiete||0) + (t.nebenkosten||0)),
 *   });
 *   <SortHeader label="Immobilie" sortKey="label" state={{sortKey, sortDir}} onToggle={toggle} />
 */
export function useTableSort(items, initialKey, initialDir = "asc", resolvers = {}) {
    const [sortKey, setSortKey] = useState(initialKey);
    const [sortDir, setSortDir] = useState(initialDir);

    const toggle = (key) => {
        if (key === sortKey) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
    };

    const sorted = useMemo(() => {
        if (!items) return [];
        if (!sortKey) return items;
        const res = resolvers[sortKey] || ((x) => x?.[sortKey]);
        const dirFactor = sortDir === "asc" ? 1 : -1;
        return [...items].sort((a, b) => {
            const av = res(a);
            const bv = res(b);
            const aNull = av === null || av === undefined || av === "";
            const bNull = bv === null || bv === undefined || bv === "";
            if (aNull && bNull) return 0;
            if (aNull) return 1;   // leere Werte immer nach unten
            if (bNull) return -1;
            if (typeof av === "number" && typeof bv === "number") return (av - bv) * dirFactor;
            // Datums-Strings ISO: lexikografischer Vergleich reicht
            const as = String(av), bs = String(bv);
            return as.localeCompare(bs, "de", { numeric: true, sensitivity: "base" }) * dirFactor;
        });
    }, [items, sortKey, sortDir, resolvers]);

    return { sortKey, sortDir, toggle, sorted };
}

/**
 * Clickable <th> wrapper for sortable columns.
 */
export const SortHeader = ({ label, sortKey, state, onToggle, align = "left", className = "" }) => {
    const isActive = state.sortKey === sortKey;
    const Icon = !isActive ? ArrowUpDown : state.sortDir === "asc" ? ArrowUp : ArrowDown;
    return (
        <th className={`font-medium px-4 py-3 select-none ${align === "right" ? "text-right" : "text-left"} ${className}`}>
            <button
                type="button"
                onClick={() => onToggle(sortKey)}
                className={`inline-flex items-center gap-1 hover:text-slate-900 transition-colors ${isActive ? "text-slate-900" : ""}`}
                data-testid={`sort-${sortKey}`}
            >
                {label}
                <Icon size={11} className={isActive ? "opacity-100" : "opacity-40"} />
            </button>
        </th>
    );
};

/** Sortiert Options alphabetisch nach deutschem Locale (für Dropdowns). */
export function sortOptions(options, getLabel = (o) => o.label ?? o.name ?? "") {
    return [...options].sort((a, b) =>
        getLabel(a).localeCompare(getLabel(b), "de", { sensitivity: "base" })
    );
}
